// /payment/success — polls the backend until the transaction is marked paid
// (backend has an inline Stripe-retrieve fallback so this usually resolves
// on the first tick even if the webhook hasn't fired yet).
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api/public` });

const money = (n, ccy) => `${(ccy || "cad").toUpperCase()} $${Number(n || 0).toFixed(2)}`;

export default function PaymentSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        setAttempts((a) => a + 1);
        if (data.payment_status !== "paid" && data.payment_status !== "failed" && data.payment_status !== "expired") {
          timerRef.current = setTimeout(poll, 2000);
        }
      } catch {
        timerRef.current = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "expired";

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 py-10 px-4" data-testid="payment-success-page">
      <div className="max-w-md mx-auto bg-white border border-neutral-200 rounded-xl shadow-sm p-8 text-center">
        {!paid && !failed && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full border-4 border-neutral-200 border-t-neutral-800 animate-spin mb-4" />
            <h1 className="text-xl font-semibold" data-testid="payment-status-processing">Confirming your payment…</h1>
            <p className="text-sm text-neutral-500 mt-2">This usually takes a couple of seconds.</p>
            <p className="text-[11px] text-neutral-400 mt-4">Attempt {attempts}</p>
          </>
        )}
        {paid && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <span className="text-emerald-700 text-2xl">✓</span>
            </div>
            <h1 className="text-2xl font-semibold" data-testid="payment-status-paid">Payment received</h1>
            <p className="text-sm text-neutral-600 mt-2">
              We&apos;ve recorded <b>{money(status.amount, status.currency)}</b> against invoice <b>{status.invoice_number}</b>.
            </p>
            <p className="text-xs text-neutral-500 mt-4">You can close this window — a receipt has been emailed to you by Stripe.</p>
          </>
        )}
        {failed && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <span className="text-red-700 text-2xl">✕</span>
            </div>
            <h1 className="text-2xl font-semibold" data-testid="payment-status-failed">Payment didn&apos;t go through</h1>
            <p className="text-sm text-neutral-600 mt-2">
              {status.payment_status === "expired" ? "This checkout session expired." : "Your payment was not completed."}
            </p>
            {status.invoice_id && (
              <Link to={`/pay/${status.invoice_id}`} className="inline-block mt-6 h-11 leading-[44px] px-6 rounded-md bg-neutral-900 text-white font-medium">
                Try again
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
